/**
 * Applies the declarative WebMCP annotations at runtime instead of serving them in the HTML.
 *
 * Chrome 151 kills the renderer when a `<form>` carrying `toolname` or `tooldescription` is
 * parsed into an inert document, which is exactly what `new DOMParser().parseFromString()`
 * builds on every quick-add fetch, variant change and section re-render. The same form is
 * harmless once it lives in the document, so the markup ships the intent as `data-webmcp-*`
 * and this promotes it to the real attributes here.
 *
 * Minimal repro of the browser bug, which crashes on its own:
 *   new DOMParser().parseFromString('<form action="/x" toolname="y"></form>', 'text/html')
 *
 * Once the bug is fixed upstream this file goes away and the attributes go back to Liquid.
 */

const NAME_ATTRIBUTE = 'data-webmcp-name';
const DESCRIPTION_ATTRIBUTE = 'data-webmcp-description';
const AUTOSUBMIT_ATTRIBUTE = 'data-webmcp-autosubmit';
const PENDING_SELECTOR = `form[${NAME_ATTRIBUTE}][${DESCRIPTION_ATTRIBUTE}]:not([toolname])`;
const MIRRORED_PRODUCT_PAGE = '#quick-add-dialog';

/**
 * Promotes every annotated form that hasn't been promoted yet.
 * @returns {void}
 */
function promoteAnnotations() {
  for (const form of document.querySelectorAll(PENDING_SELECTOR)) {
    // The quick-add modal is built from a fetched product page, so its copy of the add-to-cart
    // form carries the same intent and would register a second tool under a name that has to be
    // unique per document. The canonical form on the product page is the one that gets promoted.
    if (form.closest(MIRRORED_PRODUCT_PAGE)) continue;

    const name = form.getAttribute(NAME_ATTRIBUTE);
    const description = form.getAttribute(DESCRIPTION_ATTRIBUTE);
    if (!name || !description) continue;

    form.setAttribute('toolname', name);
    form.setAttribute('tooldescription', description);
    if (form.hasAttribute(AUTOSUBMIT_ATTRIBUTE)) form.setAttribute('toolautosubmit', '');
  }
}

let scheduled = false;

/**
 * A re-render replaces the form with markup that only carries the `data-webmcp-*` intent, so the
 * promotion has to run again. Batched into a frame because morph mutates in bursts.
 * @returns {void}
 */
function schedulePromotion() {
  if (scheduled) return;
  scheduled = true;
  requestAnimationFrame(() => {
    scheduled = false;
    promoteAnnotations();
  });
}

/**
 * The browser wires up its declarative-tool observer as part of loading the document, and an
 * attribute set before that point is simply never seen — the form ends up carrying `toolname`
 * without a tool behind it. Waiting for `load` puts the promotion after that window.
 * @returns {void}
 */
function start() {
  requestAnimationFrame(() => {
    promoteAnnotations();
    new MutationObserver(schedulePromotion).observe(document.body, { childList: true, subtree: true });
  });
}

if (document.readyState === 'complete') {
  start();
} else {
  window.addEventListener('load', start, { once: true });
}
